import { OptionStr } from 'types/common/FormTypes';
import {
  AdjustPointsFormType,
  // CreateCustomerRequest,
  CreateCustomerRequestWithPickupAddresses,
  CustomerBillingAddress,
  CustomerContact,
  CustomerCreditInfo,
  CustomerFinancialInfo,
  CustomerFormType,
  CustomerInfo,
  GetCustomerPointsResponseData,
  GetCustomerResponseData,
  PickupAddress,
  // UpdateCustomerRequest,
  UpdateCustomerRequestWithPickupAddresses,
} from 'types/customer/customerTypes';
import { GroupPrice } from 'types/MerchantTypes';
import {
  idNumToStrOrEmpty,
  toFloat,
  toInt,
  toNum,
  toStr,
} from 'utils/common/String';
import { formatToYMD } from 'utils/common/Time';
import { TFunction } from 'i18next';

export const transformCustomerFormToRequest = (form: CustomerFormType): any => {
  return {
    ...form,
    taxStatus: form.customerFinancialInfo.isTaxable ? 'taxable' : 'non-taxable',
  };
};

export const convertGroupPriceToOptions = (
  groupPrices: GroupPrice[]
): OptionStr[] => {
  return groupPrices.map((gp) => ({
    value: gp.id.toString(),
    label: gp.name,
  }));
};

export const mapToCreateCustomerRequest = (
  form: CustomerFormType
): CreateCustomerRequestWithPickupAddresses => {
  const {
    customerInfo,
    customerAddress,
    customerContact,
    customerFinancialInfo,
    customerCreditInfo,
    pickupAddresses,
  } = form;

  const provinceId =
    customerAddress.isNewAddress && customerAddress.province_id
      ? toInt(customerAddress.province_id)
      : null;

  const districtId =
    customerAddress.isNewAddress && customerAddress.district_id
      ? toInt(customerAddress.district_id)
      : null;

  const subDistrictId =
    customerAddress.isNewAddress && customerAddress.sub_district_id
      ? toInt(customerAddress.sub_district_id)
      : null;

  // const birthDate = customerInfo.birthDate?.trim() ?? '';

  const dateLimit = toInt(customerCreditInfo.dateLimit, 0);
  const receivableLimit = toFloat(customerCreditInfo.receivableLimit, 0);

  const pickUpAddrMap = pickupAddresses.map((addr) => ({
    fullname: toStr(addr.pickupName),
    phone: toStr(addr.phone),
    address: toStr(addr.address),
    state: toStr(addr.state),
    district: toStr(addr.district),
    city: toStr(addr.city),
    zip: toStr(addr.zip),
    is_default: addr.isDefault,
  }));

  const payload: CreateCustomerRequestWithPickupAddresses = {
    first_name: customerInfo.first_name?.trim() ?? '',
    last_name: customerContact.last_name?.trim() ?? '',
    account_number: customerInfo.accountNumber?.trim() ?? '',
    phone_number: customerContact.phoneNumber?.trim() ?? '',
    card_id: customerInfo.cardId?.trim() ?? '',
    branch_code: customerInfo.branchCode?.trim() ?? '',
    address_2: customerInfo.address_2?.trim() ?? '',
    // birth_date: birthDate,
    birth_date: formatToYMD(customerInfo.birthDate) ?? '',
    address_1: customerAddress.address_1?.trim() ?? '',
    city: customerAddress.city?.trim() ?? '',
    state: customerAddress.state?.trim() ?? '',
    zip: customerAddress.zip?.trim() ?? '',
    shipping_address: customerAddress.shippingAddress?.trim() ?? '',
    gender: toInt(customerInfo.gender),
    email: customerContact.email?.trim() ?? '',
    facebook: customerContact.facebook?.trim() ?? '',
    line: customerContact.line?.trim() ?? '',
    instagram: customerContact.instagram?.trim() ?? '',
    date_limit: dateLimit,
    receivable_limit: receivableLimit,
    group_price: customerFinancialInfo.groupPrice?.trim() ?? '',
    is_taxable: parseInt(customerFinancialInfo.isTaxable, 10),
    comments: customerInfo.comments?.trim() ?? '',

    province_id: provinceId,
    district_id: districtId,
    sub_district_id: subDistrictId,
    entity_type: toInt(customerInfo.entity_type),
    pickup_addresses: pickUpAddrMap,
  };

  return payload;
};

export const mapToUpdateCustomerRequest = (
  form: CustomerFormType,
  reqDeletePickupAddr: PickupAddress[]
): UpdateCustomerRequestWithPickupAddresses => {
  if (Object.keys(form).length === 0)
    return {} as unknown as UpdateCustomerRequestWithPickupAddresses;
  const map = mapToCreateCustomerRequest(form);
  if (!form.customerInfo.register_store_id) {
    return map as UpdateCustomerRequestWithPickupAddresses;
  }
  const pickup_addresses = form.pickupAddresses
    .filter((addr) => addr.id !== undefined)
    .map((addr) => ({
      id: addr.id,
      fullname: toStr(addr.pickupName),
      phone: toStr(addr.phone),
      address: toStr(addr.address),
      state: toStr(addr.state),
      district: toStr(addr.district),
      city: toStr(addr.city),
      zip: toStr(addr.zip),
      is_default: addr.isDefault,
    }));
  const new_pickup_addresses = form.pickupAddresses
    .filter((addr) => addr.id === undefined)
    .map((addr) => ({
      id: addr.id,
      fullname: toStr(addr.pickupName),
      phone: toStr(addr.phone),
      address: toStr(addr.address),
      state: toStr(addr.state),
      district: toStr(addr.district),
      city: toStr(addr.city),
      zip: toStr(addr.zip),
      is_default: addr.isDefault,
    }));
  const delete_pickup_addresses = reqDeletePickupAddr.map((addr) => ({
    id: addr.id,
    fullname: toStr(addr.pickupName),
    phone: toStr(addr.phone),
    address: toStr(addr.address),
    state: toStr(addr.state),
    district: toStr(addr.district),
    city: toStr(addr.city),
    zip: toStr(addr.zip),
    is_default: addr.isDefault,
  }));

  const payload = {
    ...map,
    register_store_id: toInt(form.customerInfo.register_store_id),
    pickup_addresses: pickup_addresses,
    new_pickup_addresses: new_pickup_addresses,
    delete_pickup_addresses: delete_pickup_addresses,
  };
  return payload;
};

// const inferIsNewAddress = (
//   provinceId?: number | null,
//   districtId?: number | null,
//   subdistrictId?: number | null
// ): boolean =>
//   !!(provinceId && provinceId > 0) ||
//   !!(districtId && districtId > 0) ||
//   !!(subdistrictId && subdistrictId > 0);

const inferIsNewAddress = (
  provinceId?: number | null,
  districtId?: number | null,
  subdistrictId?: number | null,
  city?: string | null,
  state?: string | null
): boolean =>
  (provinceId ?? 0) > 0 ||
  (districtId ?? 0) > 0 ||
  (subdistrictId ?? 0) > 0 ||
  (city === '' && state === '');

export function mapApiToCustomerForm(
  data: GetCustomerResponseData | null
): CustomerFormType {
  if (data == null) {
    return {} as unknown as CustomerFormType;
  }
  if (Object.keys(data).length === 0) {
    return {} as unknown as CustomerFormType;
  }

  const isNewAddress = inferIsNewAddress(
    data.province_id,
    data.district_id,
    data.sub_district_id,
    data.city,
    data.state
  );

  const birthDate = new Date(new Date(data.birth_date).getTime());

  const customerInfo: CustomerInfo = {
    register_store_id:
      data.register_store_id != null ? String(data.register_store_id) : '',
    entity_type: toStr(data.entity_type),
    accountNumber: toStr(data.account_number),
    first_name: toStr(data.first_name),
    cardId: toStr(data.card_id),
    branchCode: toStr(data.branch_code),
    address_2: toStr(data.address_2),
    birthDate:
      data.birth_date === ''
        ? ''
        : birthDate.toISOString().split('.')[0] + '.000Z',
    gender: toStr(data.gender),
    comments: toStr(data.comments),
  };

  const customerAddress: CustomerBillingAddress = {
    isNewAddress,
    address_1: toStr(data.address_1),
    city: toStr(data.city),
    state: toStr(data.state),
    province_id: idNumToStrOrEmpty(data.province_id),
    district_id: idNumToStrOrEmpty(data.district_id),
    sub_district_id: idNumToStrOrEmpty(data.sub_district_id),
    zip: toStr(data.zip),
    shippingAddress: toStr(data.shipping_address),
  };

  const customerContact: CustomerContact = {
    last_name: toStr(data.last_name),
    phoneNumber: toStr(data.phone_number),
    email: toStr(data.email),
    facebook: toStr(data.facebook),
    line: toStr(data.line),
    instagram: toStr(data.instagram),
  };

  const customerFinancialInfo: CustomerFinancialInfo = {
    groupPrice: toStr(data.group_price ?? ''),
    isTaxable: data.taxable.toString(),
  };

  const customerCreditInfo: CustomerCreditInfo = {
    dateLimit: String(toNum(data.date_limit)),
    receivableLimit: String(toNum(data.receivable_limit)),
  };

  const pickupAddresses = data.pickup_addresses.map((addr) => ({
    id: addr.id,
    pickupName: toStr(addr.fullname),
    phone: toStr(addr.phone),
    address: toStr(addr.address),
    state: toStr(addr.state),
    district: toStr(addr.district),
    city: toStr(addr.city),
    zip: toStr(addr.zip),
    isDefault: addr.is_default,
  }));

  return {
    customerInfo,
    customerAddress,
    customerContact,
    customerFinancialInfo,
    customerCreditInfo,
    pickupAddresses,
  };
}

export function mapApiAdjustPointsForm(
  data: GetCustomerPointsResponseData | null
): AdjustPointsFormType {
  if (data == null) {
    return {} as unknown as AdjustPointsFormType;
  }
  if (Object.keys(data).length === 0) {
    return {} as unknown as AdjustPointsFormType;
  }

  return {
    customerName: data.first_name,
    accountNumber: data.account_number,
    historyPoints: data.point_balance,
    type: 'earn',
    point: '',
    comments: '',
  };
}

export function mapPointsType(text: string, t: TFunction): string {
  if (!text) return '';
  switch (text) {
    case 'earn':
      return t('Top up');
    case 'burn':
      return t('Deduct');
    default:
      return '';
  }
}
